import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { BiChevronDown } from 'react-icons/bi'
import { AiOutlineSearch } from 'react-icons/ai'
import { MdAddBox } from 'react-icons/md'
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../hooks';
import { LangArrayToObjKeyValue } from '../../utils/global';
import { useCurrent } from '../../hooks';
import { useQuery } from '@tanstack/react-query';


const EMPTY_PRODUCTS = [];
const LIST_MAX_HEIGHT = 240;

// Search ignores case and the usual Arabic spelling variants (أ إ آ -> ا, ة -> ه, ى -> ي).
export const normalizeSearch = (value) => String(value || '')
    .trimStart()
    .toLocaleLowerCase()
    .replace(/[أإآ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي');

const productDisplayName = (product, langId) =>
    LangArrayToObjKeyValue(product?.productName)?.[langId] || product?.productName?.[0]?.text || product?.productName?.[1]?.text || '';

const isSalesProduct = (product) => product?.productType === 'Sales' || product?.productType === 'SalesAndPurchases';

// The name (in the reader's language first) that starts with what was typed, for the inline completion.
const completionName = (product, typed, langId) => {
    if (/^\s/.test(typed)) return '';
    const query = normalizeSearch(typed);
    const names = [productDisplayName(product, langId), product?.productName?.[0]?.text, product?.productName?.[1]?.text]
        .map((name) => String(name || '').trim());
    return names.find((name) => name.length > typed.length && normalizeSearch(name).startsWith(query)) || '';
};

// Sales products matching `typed`: names that start with it first (the first one gives the inline
// completion), then names, barcodes or codes that contain it.
export const matchProducts = (products, typed) => {
    const query = normalizeSearch(typed);
    if (!query) return [];
    const starts = [];
    const contains = [];
    products.forEach((product) => {
        if (!isSalesProduct(product)) return;
        const names = [product?.productName?.[0]?.text, product?.productName?.[1]?.text].map(normalizeSearch);
        const codes = [product?.productBarcode, product?.productCode].map(normalizeSearch);
        if (names.some((name) => name.startsWith(query))) starts.push(product);
        else if ([...names, ...codes].some((value) => value.includes(query))) contains.push(product);
    });
    return [...starts, ...contains];
};

const MwSelectorProduct = ({ onChangeCallback, onCreateProduct, reloadProductList, withLabel = false, invalid = false, initalValue, disabled = false, hideSearchAndAdd = false }) => {
    const [t] = useTranslation('global')
    const { currentLangId } = useCurrent();
    const { getUser } = useUser();
    // typedValue: what the user typed (searched, and sent as a typed product).
    // inputValue: what the field shows = typedValue + the highlighted completion, if any.
    const [typedValue, setTypedValue] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [selectedItem, setSelectedItem] = useState();
    const [open, setOpen] = useState(false);
    const lastTypedValueRef = useRef('');
    const inputRef = useRef(null);
    const completionRef = useRef(null); // { start, end } of the highlighted part of inputValue
    const listId = useId();
    const tenantId = getUser?.tenant_id;
    const companyId = getUser?.company_id;

    const {
        data: productsData,
        refetch,
    } = useQuery({
        queryKey: ['products', tenantId, companyId],
        enabled: !!tenantId && !!companyId && !disabled,
        staleTime: 30 * 60 * 1000,
        queryFn: async () => {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${process.env.REACT_APP_PRODUCTS_API_SERVER_BASE_URL}/search/All`,
                {
                    tenantId,
                    companyId,
                },
                {
                    headers: token
                        ? {
                            'Content-Type': 'application/json; charset=utf-8',
                            Authorization: `Bearer ${token}`,
                        }
                        : { 'Content-Type': 'application/json; charset=utf-8' },
                }
            );

            return response?.data?.data || [];
        },
    });

    const products = productsData || EMPTY_PRODUCTS;

    useEffect(() => {
        if (selectedItem) {
            onChangeCallback(selectedItem);
        }
    }, [selectedItem, onChangeCallback]);



    useEffect(() => {
        if (reloadProductList) {
            refetch();
        }
    }, [reloadProductList, refetch]);

    const showText = useCallback((typed, shown = typed) => {
        setTypedValue((prev) => (prev === typed ? prev : typed));
        setInputValue((prev) => (prev === shown ? prev : shown));
    }, []);

    useEffect(() => {
        if (initalValue && typeof initalValue === 'object' && initalValue.productId) {
            completionRef.current = null;
            setSelectedItem(initalValue);
            const nextValue =
                LangArrayToObjKeyValue(initalValue.productName)?.[currentLangId] ||
                initalValue.productName[0]?.text ||
                initalValue.productName[1]?.text ||
                '';
            showText(nextValue);
            return;
        }
        const nextValue = typeof initalValue === 'string' ? initalValue : '';
        // Our own typing coming back from the row: keep the field (and its completion) as it is.
        if (nextValue === lastTypedValueRef.current) return;
        // Otherwise the row changed (product picked, row reset or moved): show its name, even when
        // empty, so no text of another row is left behind.
        completionRef.current = null;
        lastTypedValueRef.current = nextValue;
        setSelectedItem(null);
        showText(nextValue);
    }, [initalValue, currentLangId, showText]);

    const productsFilter = useMemo(() => matchProducts(products, typedValue), [typedValue, products]);

    // تم إضافة دالة لمعالجة اختيار المنتج
    const handleSelectProduct = (product) => {
        completionRef.current = null;
        setSelectedItem(product);
        setOpen(false);
        showText('');
    };

    // Shows `product`'s name after the typed text, with the added part selected (like the browser's
    // address bar): typing the next letter replaces it, Backspace removes it, Enter / Tab picks it.
    // selectionTick re-renders even when the shown text is unchanged (typing the next letter of the
    // completion), so the highlight is put back after React restores the field's value.
    const [selectionTick, setSelectionTick] = useState(0);
    const showCompletion = (typed, product) => {
        const name = product ? completionName(product, typed, currentLangId) : '';
        if (!name) {
            completionRef.current = null;
            showText(typed);
            return;
        }
        completionRef.current = { start: typed.length, end: name.length };
        showText(typed, typed + name.slice(typed.length));
        setSelectionTick((tick) => tick + 1);
    };

    useLayoutEffect(() => {
        const range = completionRef.current;
        const input = inputRef.current;
        if (!range || !input || document.activeElement !== input) return;
        try { input.setSelectionRange(range.start, range.end, 'backward'); } catch { }
    }, [inputValue, selectionTick]);

    // A completion that was not picked is dropped when leaving the field.
    const dropCompletion = () => {
        if (!completionRef.current) return;
        completionRef.current = null;
        setInputValue(typedValue);
    };

    // Autocomplete: the suggestions open while typing; arrows move, Enter picks, Escape or a click
    // outside closes. Enter is kept from submitting the invoice form while a suggestion is open.
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const listRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    useEffect(() => { setActiveIndex(0); }, [typedValue]);
    useEffect(() => {
        if (!open) return undefined;
        const onOutside = (event) => {
            if (wrapperRef.current?.contains(event.target) || listRef.current?.contains(event.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onOutside);
        return () => document.removeEventListener('mousedown', onOutside);
    }, [open]);
    useEffect(() => {
        listRef.current?.querySelector(`[data-index="${activeIndex}"]`)?.scrollIntoView?.({ block: 'nearest' });
    }, [activeIndex]);

    // The list is drawn over the page (portal + fixed position) so the items table's scroll area
    // cannot cut it; it opens upwards when there is no room under the field.
    const showList = open && productsFilter.length > 0;
    const [listPos, setListPos] = useState(null);
    const updateListPosition = useCallback(() => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const width = Math.min(Math.max(rect.width, 320), viewportWidth - 16);
        const isRtl = getComputedStyle(triggerRef.current).direction === 'rtl';
        const left = Math.min(Math.max(8, isRtl ? rect.right - width : rect.left), viewportWidth - width - 8);
        const below = viewportHeight - rect.bottom - 8;
        const above = rect.top - 8;
        const openUp = below < Math.min(LIST_MAX_HEIGHT, 160) && above > below;
        setListPos({
            left,
            width,
            top: openUp ? undefined : rect.bottom + 2,
            bottom: openUp ? viewportHeight - rect.top + 2 : undefined,
            maxHeight: Math.max(120, Math.min(LIST_MAX_HEIGHT, openUp ? above : below)),
            openUp,
        });
    }, []);
    useLayoutEffect(() => {
        if (!showList) return undefined;
        updateListPosition();
        window.addEventListener('resize', updateListPosition);
        window.addEventListener('scroll', updateListPosition, true);
        return () => {
            window.removeEventListener('resize', updateListPosition);
            window.removeEventListener('scroll', updateListPosition, true);
        };
    }, [showList, updateListPosition]);

    const onInputKeyDown = (event) => {
        const count = productsFilter.length;
        if (event.key === 'ArrowDown' && count) {
            event.preventDefault();
            const next = open ? (activeIndex + 1) % count : activeIndex;
            setOpen(true);
            setActiveIndex(next);
            showCompletion(typedValue, productsFilter[next]);
        } else if (event.key === 'ArrowUp' && count) {
            event.preventDefault();
            const next = (activeIndex - 1 + count) % count;
            setActiveIndex(next);
            showCompletion(typedValue, productsFilter[next]);
        } else if (event.key === 'Enter' && open && count) {
            event.preventDefault();
            handleSelectProduct(productsFilter[Math.min(activeIndex, count - 1)]);
        } else if (event.key === 'Tab' && completionRef.current && count) {
            // Tab takes the completed product and moves on to the next field.
            handleSelectProduct(productsFilter[Math.min(activeIndex, count - 1)]);
        } else if (event.key === 'Escape') {
            setOpen(false);
            dropCompletion();
        }
    };

    const onTyping = (e) => {
        const nextValue = e.target.value;
        const deleting = /^delete/.test(e.nativeEvent?.inputType || '');
        setSelectedItem(null);
        setOpen(Boolean(nextValue));
        setActiveIndex(0);
        // No completion while deleting (Backspace removes the highlighted part first) or composing.
        if (deleting || e.nativeEvent?.isComposing || !nextValue) {
            completionRef.current = null;
            showText(nextValue);
        } else {
            showCompletion(nextValue, matchProducts(products, nextValue)[0]);
        }
        // The row keeps what was typed (a product name not in the list is saved as typed).
        if (lastTypedValueRef.current !== nextValue) {
            onChangeCallback(nextValue);
            lastTypedValueRef.current = nextValue;
        }
    };

    // تم إضافة دالة لمعالجة تغيير النص في الإدخال
    const handleInputChange = (value) => {
        completionRef.current = null;
        showText(value);
        // لا تقم باستدعاء onChangeCallback هنا
        // نتيح للمستخدم البحث في المنتجات دون إرسال طلبات للسيرفر
    };

    const addProductHandler = () => {
        setOpen(false);
        onCreateProduct(true);
    };

    // استدعاء onChangeCallback فقط عند الضغط على زر البحث أو Enter
    const handleSearch = () => {
        if (typedValue) {
            onChangeCallback(typedValue);
        }
    };

    const inputClass = `finance-input flex-1 w-full rounded-md border bg-white h-9 text-xs text-[#261759] outline-none transition text-start px-3 border-0 font-bold ${invalid ? 'border-red-500 focus:border-red-600 focus:ring-red-100' : 'border-[#d7dce3] focus:border-[#261759] focus:ring-[#e7f7d6]'} focus:ring-2 disabled:bg-[#f1f5f9] disabled:text-[#8b93a1]`;

    const list = showList && listPos && (
        <ul
            ref={listRef}
            id={listId}
            role='listbox'
            className={`mw-product-suggestions fixed z-[1000] overflow-y-auto bg-white dark:bg-slate-700 border border-slate-200 shadow-lg ${listPos.openUp ? 'rounded-t-lg' : 'rounded-b-lg'}`}
            style={{ left: listPos.left, width: listPos.width, top: listPos.top, bottom: listPos.bottom, maxHeight: listPos.maxHeight }}
        >
            {!hideSearchAndAdd && (<div className='sticky top-0 p-3 bg-slate-50 dark:bg-slate-700'>
                <div className='text-end'>
                    <div className=' text-sky-700 absolute top-6 end-5' onClick={addProductHandler} >
                        <MdAddBox size={24} />
                    </div>
                </div>
                <div className='flex items-center justify-between px-2 dark:bg-slate-700 bg-white rounded'>
                    <div onClick={handleSearch} style={{ cursor: 'pointer' }}>
                        <AiOutlineSearch size={18} className='text-gray-700' />
                    </div>
                    <input
                        type="text"
                        placeholder='Enter Product Name'
                        className='placeholder:text-grray-700 p-2 outline-none  w-full text-sm dark:bg-slate-700 bg-white'
                        value={typedValue}
                        onChange={(e) => handleInputChange(e.target.value.toLowerCase())}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                    />
                </div>
            </div>)}
            <div>
                {
                    productsFilter.map((product, productIndex) => {
                        const productName = productDisplayName(product, currentLangId)
                        let productName2 = ""
                        let productDesc = ""
                        if (productName === product?.productName[0]?.text) {
                            productName2 = product?.productName[1]?.text
                            productDesc = product?.productDesc?.[0]?.text
                        } else {
                            productName2 = product?.productName[0]?.text
                            productDesc = product?.productDesc?.[1]?.text
                        }
                        return <li
                            key={product._id}
                            data-index={productIndex}
                            role='option'
                            aria-selected={productIndex === activeIndex}
                            onMouseEnter={() => setActiveIndex(productIndex)}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelectProduct(product)}
                            className={`mw-product-option ps-5 py-1 border-b border-slate-100 text-sm cursor-pointer ${productIndex === activeIndex ? 'mw-product-option-active' : ''}`}>
                            <div className='flex gap-5 justify-between'>
                                <div>
                                    <div className='mw-product-option-name'> {productName} </div>
                                    <div className='mw-product-option-sub text-xs'>  {productName2} </div>
                                    <div className='mw-product-option-sub text-xs'>{productDesc}</div>
                                    <div className='mw-product-option-sub text-xs'>
                                        {product?.productBarcode && (
                                            <span className='me-2'>
                                                {t('common.barcode')}: {product.productBarcode}
                                            </span>
                                        )}
                                        {product?.productCode && (
                                            <span>
                                                {t('common.code')}: {product.productCode}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className='mw-product-option-sub text-xs font-bold p-3'>
                                    <div className='flex flex-wrap gap-1 mb-1'>
                                        {product?.productBrand?.brandName?.length > 0 && <span className='mw-product-option-chip rounded-lg px-2 py-1 inline-block'>
                                            {LangArrayToObjKeyValue(product?.productBrand?.brandName)?.[currentLangId] || product?.productBrand?.brandName[0]?.text || product?.productBrand?.brandName[1]?.text}
                                        </span>}
                                        {product?.productCategory?.[0]?.categoryName?.length > 0 && <span className='mw-product-option-chip rounded-lg px-2 py-1 inline-block'>
                                            {LangArrayToObjKeyValue(product?.productCategory[0]?.categoryName)?.[currentLangId] || product?.productCategory[0]?.categoryName[0]?.text || product?.productCategory[0]?.categoryName[1]?.text}
                                        </span>}
                                    </div>
                                    <span className='mw-product-option-qty text-[10px] rounded-lg px-2 py-1 inline-block mt-1'>
                                        {t('common.qty')}: {product?.qty || 0}
                                    </span>
                                </div>
                            </div>
                        </li>
                    })
                }
            </div>
        </ul>
    );

    return (
        <>
            <div ref={wrapperRef} className='relative mw-product-selector'>
                {withLabel && <label className={`text-xs text-slate-500 `} >{t('invoice.selectProduct')}</label>}
                <div
                    ref={triggerRef}
                    onClick={() => {
                        !disabled && setOpen(true);
                    }}
                    className={` h-9 bg-white dark:bg-slate-700 w-full border flex items-center text-sm  justify-between rounded ${invalid ? 'border border-red-400' : ''} ${disabled ? 'text-slate-200' : 'text-slate-300'} `}
                >
                    {
                        selectedItem ?
                            <span className={` px-2 ${disabled ? ' text-slate-300' : 'text-slate-500'}`}>{productDisplayName(selectedItem, currentLangId)}</span> :
                            <input
                                ref={inputRef}
                                type='text'
                                autoComplete='off'
                                role='combobox'
                                aria-expanded={showList}
                                aria-controls={listId}
                                aria-autocomplete='both'
                                disabled={disabled}
                                value={inputValue}
                                className={inputClass}
                                onKeyDown={onInputKeyDown}
                                onChange={onTyping}
                                onBlur={dropCompletion}
                            />
                    }
                    <BiChevronDown
                        size={20}
                        className='mw-selector-product-chevron border rounded m-2 text-[#2d6297]'
                        onClick={(e) => { e.stopPropagation(); !disabled && setOpen(!open); }}
                    />
                </div>
            </div>
            {list && createPortal(list, document.body)}
        </>
    )
}

export default MwSelectorProduct;
